import { useEffect, useId, useMemo, useRef, useState } from "react";
import { parsePastedNames } from "../../utils/parsePastedNames";
import shared from "../cmsShared.module.css";
import styles from "./PasteNamesDialog.module.css";

export type PasteNamesDialogProps = {
  title: string;
  textBoxLabel: string;
  itemNoun: { one: string; other: string };
  existing: string[];
  onConfirm: (names: string[]) => void;
  onCancel: () => void;
};

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function keepTabInside(event: KeyboardEvent, dialog: HTMLElement) {
  const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (!first || !last) {
    event.preventDefault();
    dialog.focus();
    return;
  }
  const active = document.activeElement;
  const outside = !(active instanceof Node) || !dialog.contains(active);
  if (event.shiftKey && (outside || active === first || active === dialog)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (outside || active === last)) {
    event.preventDefault();
    first.focus();
  }
}

export function PasteNamesDialog({
  title,
  textBoxLabel,
  itemNoun,
  existing,
  onConfirm,
  onCancel,
}: PasteNamesDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const titleId = useId();
  const textareaId = useId();
  const [text, setText] = useState("");
  const { names, alreadyListed, repeated } = useMemo(
    () => parsePastedNames(text, existing),
    [text, existing],
  );
  const noun = names.length === 1 ? itemNoun.one : itemNoun.other;

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
      if (e.key === "Tab" && dialogRef.current) keepTabInside(e, dialogRef.current);
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onCancel]);

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    textareaRef.current?.focus();
    return () => {
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, []);

  return (
    <div className={styles.overlay}>
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <h3 id={titleId} className={styles.title}>
          {title}
        </h3>
        <label htmlFor={textareaId} className={styles.label}>
          {textBoxLabel}
        </label>
        <textarea
          id={textareaId}
          ref={textareaRef}
          className={styles.textarea}
          rows={10}
          value={text}
          onChange={e => setText(e.target.value)}
        />
        <p role="status" className={styles.status}>
          {names.length} {noun} will be added
          {alreadyListed > 0 && `, ${alreadyListed} already listed`}
          {repeated > 0 && `, ${repeated} repeated in the paste`}
        </p>
        <div className={styles.actions}>
          <button type="button" className={shared.discardButton} onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className={`${shared.saveButton} ${styles.confirmButton}`}
            disabled={names.length === 0}
            onClick={() => onConfirm(names)}
          >
            Add {names.length} {noun}
          </button>
        </div>
      </div>
    </div>
  );
}
